import { z } from 'zod';

import { isTruthy } from '../../../utils.js';
import { BaseFlowBlockSchema, BlockType } from '../base/baseSchema.js';
import { BlockValueNumberSchema } from '../base/blockValueNumberSchema.js';
import { BlockValueStringSchema } from '../base/blockValueStringSchema.js';

export enum FileConversionFormat {
  Base64 = 'Base64',
  Pdf = 'Pdf',
  Base64ToFile = 'Base64ToFile',
}

export enum FileConversionOperation {
  Convert = 'Convert',
  SplitPdf = 'SplitPdf',
  CombinePdf = 'CombinePdf',
  ExtractPages = 'ExtractPages',
}

export enum SplitMode {
  Range = 'Range',
  EveryXPages = 'EveryXPages',
}

const ConvertConfigSchema = z.object({
  operation: z.literal(FileConversionOperation.Convert),
  fileReference: z.string().optional(),
  base64Payload: z.string().optional(),
  format: z.nativeEnum(FileConversionFormat).default(FileConversionFormat.Base64),
});

const SplitPdfConfigSchema = z.object({
  operation: z.literal(FileConversionOperation.SplitPdf),
  fileReference: z.string().min(1),
  splitMode: z.nativeEnum(SplitMode).default(SplitMode.Range),
  pageRanges: z.string().optional(),
  pageCount: BlockValueNumberSchema({
    numberValidationSchema: z.number().int().positive(),
  }).optional(),
});

const CombinePdfConfigSchema = z.object({
  operation: z.literal(FileConversionOperation.CombinePdf),
  fileReferences: z.string().min(1),
});

const MAX_PAGE_NUMBERS_INPUT_LENGTH = 128;

const ExtractPagesConfigSchema = z.object({
  operation: z.literal(FileConversionOperation.ExtractPages),
  fileReference: z.string().min(1),
  pageNumbers: BlockValueStringSchema({
    stringValidationSchema: z.string().min(1).max(MAX_PAGE_NUMBERS_INPUT_LENGTH),
  }),
});

export type ConvertConfig = z.infer<typeof ConvertConfigSchema>;
export type SplitPdfConfig = z.infer<typeof SplitPdfConfigSchema>;
export type CombinePdfConfig = z.infer<typeof CombinePdfConfigSchema>;
export type ExtractPagesConfig = z.infer<typeof ExtractPagesConfigSchema>;

type LegacyConvertConfig = Omit<ConvertConfig, 'operation'>;
type FileConversionConfigInput =
  | ConvertConfig
  | SplitPdfConfig
  | CombinePdfConfig
  | ExtractPagesConfig
  | LegacyConvertConfig;

function withDefaultOperation(
  value: FileConversionConfigInput
): ConvertConfig | SplitPdfConfig | CombinePdfConfig | ExtractPagesConfig {
  if ('operation' in value) {
    return value;
  }
  return { ...value, operation: FileConversionOperation.Convert };
}

export const FileConversionBlockConfigSchema = z
  .preprocess(
    (value) => withDefaultOperation(value as FileConversionConfigInput),
    z.discriminatedUnion('operation', [
      ConvertConfigSchema,
      SplitPdfConfigSchema,
      CombinePdfConfigSchema,
      ExtractPagesConfigSchema,
    ])
  )
  .superRefine((config, ctx) => {
    if (config.operation === FileConversionOperation.Convert) {
      if (config.format === FileConversionFormat.Base64ToFile) {
        if (!isTruthy(config?.base64Payload?.trim())) {
          ctx.addIssue({
            code: 'custom',
            message: 'Please provide a Base64 payload to convert.',
            path: ['base64Payload'],
          });
        }
        if (isTruthy(config?.fileReference?.trim())) {
          ctx.addIssue({
            code: 'custom',
            message: 'Remove the file reference when converting from Base64.',
            path: ['fileReference'],
          });
        }
      } else {
        if (!isTruthy(config?.fileReference?.trim())) {
          ctx.addIssue({
            code: 'custom',
            message: 'Please provide a file reference to convert.',
            path: ['fileReference'],
          });
        }
        if (isTruthy(config?.base64Payload?.trim())) {
          ctx.addIssue({
            code: 'custom',
            message: 'Remove the Base64 payload when converting a file.',
            path: ['base64Payload'],
          });
        }
      }
    }

    if (config.operation === FileConversionOperation.SplitPdf) {
      if (config.splitMode === SplitMode.Range && !isTruthy(config?.pageRanges?.trim())) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide the page ranges to split.',
          path: ['pageRanges'],
        });
      }
      if (config.splitMode === SplitMode.EveryXPages && !isTruthy(config?.pageCount)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide the number of pages per file.',
          path: ['pageCount'],
        });
      }
    }

    if (config.operation === FileConversionOperation.CombinePdf) {
      if (!isTruthy(config?.fileReferences?.trim())) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide file references to combine.',
          path: ['fileReferences'],
        });
      }
    }

    if (config.operation === FileConversionOperation.ExtractPages) {
      if (!isTruthy(config?.fileReference?.trim())) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide a file reference to extract pages from.',
          path: ['fileReference'],
        });
      }
      if (!isTruthy(config?.pageNumbers?.trim())) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please provide the page numbers to extract. e.g. 1,2,3,5 or 1,3-5,7',
          path: ['pageNumbers'],
        });
      }
    }
  });

export const FileConversionBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.FileConversion),
  config: FileConversionBlockConfigSchema,
});

export type FileConversionBlock = z.infer<typeof FileConversionBlockSchema>;
